/**
 * Hook para buscar estatísticas da fila de lembretes (KPI cards)
 * Utiliza a RPC get_reminder_queue_stats
 * 
 * @version 1.1.0
 * RELEASE: 2026-03-29 v1.0.0 - Criação do hook
 * RELEASE: 2026-03-29 v1.1.0 - Suporte a filtros para sincronizar KPIs com listagem
 */

import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { createLogger } from '@/lib/logger';
import type { ReminderQueueFilters } from '@/hooks/useReminderQueueItems';

const logger = createLogger('ReminderQueueStats');

/** Estatísticas retornadas pela RPC */
export interface ReminderQueueStats {
  total: number;
  pending: number;
  sent: number;
  failed: number;
  cancelled: number;
  skipped: number;
  processing: number;
  read_count: number;
}

/** Valores padrão quando não há dados */
const EMPTY_STATS: ReminderQueueStats = {
  total: 0, pending: 0, sent: 0, failed: 0,
  cancelled: 0, skipped: 0, processing: 0, read_count: 0,
};

/**
 * Hook para buscar estatísticas da fila de lembretes
 * 
 * @param ruleId - ID da regra de lembrete
 * @param enabled - Se a query deve ser executada (lazy loading)
 * @param filters - Filtros opcionais para sincronizar KPIs com a listagem
 */
export function useReminderQueueStats(
  ruleId: string | undefined,
  enabled: boolean = true,
  filters?: ReminderQueueFilters
) {
  const { currentTenant } = useAuth();

  const query = useQuery({
    queryKey: ['reminder-queue-stats', ruleId, currentTenant?.id, filters],
    queryFn: async (): Promise<ReminderQueueStats> => {
      logger.debug('Buscando estatísticas da fila de lembretes:', { ruleId, filters });

      const { data, error } = await (supabase.rpc as any)(
        'get_reminder_queue_stats',
        {
          p_rule_id: ruleId,
          p_tenant_hint: currentTenant?.id || null,
          // v1.1.0: Filtros opcionais
          p_status: filters?.status || null,
          p_search: filters?.search || null,
          p_created_from: filters?.createdFrom || null,
          p_created_to: filters?.createdTo || null,
          p_remind_from: filters?.remindFrom || null,
          p_remind_to: filters?.remindTo || null,
          p_sent_from: filters?.sentFrom || null,
          p_sent_to: filters?.sentTo || null,
        }
      );

      if (error) {
        logger.error('Erro ao buscar estatísticas:', error);
        throw error;
      }

      const row = (data || [])[0];
      if (!row) return EMPTY_STATS;

      return {
        total: Number(row.total) || 0,
        pending: Number(row.pending) || 0,
        sent: Number(row.sent) || 0,
        failed: Number(row.failed) || 0,
        cancelled: Number(row.cancelled) || 0,
        skipped: Number(row.skipped) || 0,
        processing: Number(row.processing) || 0,
        read_count: Number(row.read_count) || 0,
      };
    },
    enabled: !!ruleId && !!currentTenant?.id && enabled,
    staleTime: 30 * 1000,
    gcTime: 5 * 60 * 1000,
  });

  return {
    stats: query.data || EMPTY_STATS,
    isLoading: query.isLoading,
    error: query.error,
  };
}
